import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  ClockIcon,
  ChatBubbleBottomCenterTextIcon,
  UserGroupIcon,
  RocketLaunchIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: 'General Inquiry',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: 'General Inquiry',
      message: ''
    });
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const slideIn = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: 'Phone',
      details: ['+1 (720) 679-7569'],
      description: 'Monday through Friday, 9am-5pm MST',
      bgColor: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      icon: EnvelopeIcon,
      title: 'Email',
      details: ['nailthecosthelp@gmail.com', 'nailthecostsales@gmail.com'],
      description: 'We aim to respond within 24 hours',
      bgColor: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      icon: MapPinIcon,
      title: 'Office',
      details: ['123 Business Avenue', 'Suite 200', 'Boston, MA 02110'],
      description: 'Come visit us!',
      bgColor: 'bg-emerald-50',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    },
    {
      icon: ClockIcon,
      title: 'Hours',
      details: ['Monday - Friday: 9AM - 5PM MST'],
      description: 'Weekend support available for emergencies',
      bgColor: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600'
    }
  ];

  const features = [
    {
      icon: ChatBubbleBottomCenterTextIcon,
      title: '24/7 Support',
      description: 'Our dedicated team is here to help you succeed',
      color: 'text-blue-600'
    },
    {
      icon: UserGroupIcon,
      title: 'Personal Approach',
      description: 'Get personalized solutions for your business',
      color: 'text-purple-600'
    },
    {
      icon: RocketLaunchIcon,
      title: 'Quick Response',
      description: 'Fast and efficient communication',
      color: 'text-emerald-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 to-secondary-900">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url('/grid.svg')`,
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[600px] h-[600px] rounded-full bg-secondary-500/20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[600px] h-[600px] rounded-full bg-primary-500/20 blur-3xl"></div>
        
        <div className="relative container mx-auto px-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto space-y-6"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight"
            >
              Let's Build Your <span className="text-primary-400">Success Story</span>
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl text-gray-200 mb-12"
            >
              Have questions about NailTheCost? Our team is here to help you find the perfect solution for your decking business.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 -mt-16 relative">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                className="group relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="absolute -inset-2 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className={`relative bg-white backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-200/50 transform transition-all duration-300 hover:-translate-y-1`}>
                  <div className={`w-12 h-12 ${info.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                    <info.icon className={`w-6 h-6 ${info.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{info.title}</h3>
                  {info.details.map((detail, i) => (
                    <p key={i} className="text-gray-600">{detail}</p>
                  ))}
                  <p className="text-sm text-gray-500 mt-2">{info.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/5 to-primary-900/5">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url('/grid.svg')`,
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200/50 backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="grid md:grid-cols-5">
                <div className="md:col-span-2 bg-gradient-to-br from-primary-600 to-secondary-600 p-8 text-white relative overflow-hidden">
                  <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `url('/grid.svg')`,
                    backgroundSize: '20px 20px'
                  }}></div>
                  <div className="relative">
                    <h2 className="text-2xl font-bold mb-6">Get Started Today</h2>
                    <p className="mb-8 text-white/90">Fill out the form and our team will get back to you within 24 hours.</p>
                    <ul className="space-y-4">
                      {features.map((feature, index) => (
                        <motion.li 
                          key={feature.title}
                          className="flex items-center space-x-3"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <feature.icon className="w-5 h-5 text-primary-300" />
                          <span className="text-white/90">{feature.description}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="md:col-span-3 p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h2>
                  
                  {isSubmitted ? (
                    <motion.div 
                      className="bg-green-50 border border-green-200 rounded-xl p-8 text-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircleIcon className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-green-800 mb-2">Thank You!</h3>
                      <p className="text-green-700 mb-6">
                        We've received your message and will get back to you within 24 hours.
                      </p>
                      <button
                        onClick={() => setIsSubmitted(false)}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                      >
                        Send Another Message
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form 
                      onSubmit={handleSubmit}
                      className="space-y-6"
                      initial="hidden"
                      animate="visible"
                      variants={staggerContainer}
                    >
                      <div className="grid md:grid-cols-2 gap-6">
                        <motion.div variants={fadeIn}>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                            Name *
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 backdrop-blur-sm"
                          />
                        </motion.div>
                        
                        <motion.div variants={fadeIn}>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 backdrop-blur-sm"
                          />
                        </motion.div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <motion.div variants={fadeIn}>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Phone
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 backdrop-blur-sm"
                          />
                        </motion.div>
                        
                        <motion.div variants={fadeIn}>
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                            Company
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 backdrop-blur-sm"
                          />
                        </motion.div>
                      </div>

                      <motion.div variants={fadeIn}>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                          Subject *
                        </label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 backdrop-blur-sm"
                        >
                          <option value="General Inquiry">General Inquiry</option>
                          <option value="Sales Question">Sales Question</option>
                          <option value="Technical Support">Technical Support</option>
                          <option value="Partnership Opportunity">Partnership Opportunity</option>
                          <option value="Feature Request">Feature Request</option>
                        </select>
                      </motion.div>

                      <motion.div variants={fadeIn}>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                          Message *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows="5"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 backdrop-blur-sm"
                        ></textarea>
                      </motion.div>

                      <motion.div variants={fadeIn}>
                        <button
                          type="submit"
                          className="w-full px-6 py-3 border border-transparent rounded-lg shadow-lg text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 transform hover:-translate-y-1"
                        >
                          Send Message
                        </button>
                      </motion.div>
                    </motion.form>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200/50 backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-3xl blur-xl"></div>
              <div className="relative aspect-w-16 aspect-h-9">
                <iframe
                  title="Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2948.8442938869594!2d-71.0786286!3d42.3586617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e37a0d715622b3%3A0x5b3dd9972d41419f!2sBoston%2C%20MA!5e0!3m2!1sen!2sus!4v1709766537185!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-xl"
                ></iframe>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 
